class SubcollectionsNav {
  constructor() {
    this.isLoading = false;
    this.abortController = null;
    this.onClick = this.onClick.bind(this);
    this.onPopState = this.onPopState.bind(this);

    document.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);
  }

  get container() {
    return document.querySelector('[data-subcollections-nav]');
  }

  getSectionId(section) {
    return section.id.replace('shopify-section-', '');
  }

  getTargetSections(doc) {
    const main = doc.querySelector('#MainContent');
    if (!main) return [];

    const directSections = Array.from(main.querySelectorAll(':scope > .shopify-section'));
    if (directSections.length) return directSections;

    return Array.from(main.querySelectorAll('.shopify-section'));
  }

  runScripts(section) {
    section.querySelectorAll('script').forEach((script) => {
      const newScript = document.createElement('script');
      Array.from(script.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));
      newScript.textContent = script.textContent;
      script.replaceWith(newScript);
    });
  }

  insertSectionHtml(html, insertAfter) {
    const template = document.createElement('div');
    template.innerHTML = html;
    const sectionEl = template.firstElementChild;
    if (!sectionEl) return insertAfter;

    insertAfter.insertAdjacentElement('afterend', sectionEl);
    this.runScripts(sectionEl);
    return sectionEl;
  }

  onClick(event) {
    const container = this.container;
    if (!container) return;

    const link = event.target.closest('.subcollection-item');
    if (!link || !container.contains(link)) return;

    const href = link.getAttribute('href');
    if (!href || href.startsWith('#') || link.target === '_blank') return;

    let url;
    try {
      url = new URL(href, window.location.origin);
    } catch {
      return;
    }

    if (url.origin !== window.location.origin) return;
    if (!url.pathname.includes('/collections/')) return;

    event.preventDefault();
    this.loadCollection(url.pathname + url.search);
  }

  onPopState(event) {
    if (!event.state?.subcollectionUrl) return;
    this.loadCollection(event.state.subcollectionUrl, false);
  }

  setLoading(loading) {
    this.isLoading = loading;
    const container = this.container;
    if (!container) return;

    container.classList.toggle('is-loading', loading);
    container.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  updateBreadcrumbs(doc) {
    const newBreadcrumbs = doc.querySelector('.breadcrumbs');
    const currentBreadcrumbs = document.querySelector('.breadcrumbs');

    if (newBreadcrumbs && currentBreadcrumbs) {
      currentBreadcrumbs.replaceWith(newBreadcrumbs.cloneNode(true));
    }
  }

  updateTitle(doc) {
    const newTitle = doc.querySelector('title');
    if (newTitle) {
      document.title = newTitle.textContent;
    }
  }

  syncMainSections(sectionIds, sectionsData) {
    const main = document.getElementById('MainContent');
    if (!main) return false;

    const breadcrumbs = main.querySelector('.breadcrumbs');
    const sectionsToInsert = sectionIds
      .map((id) => sectionsData[id])
      .filter(Boolean);

    if (!sectionsToInsert.length) return false;

    main.querySelectorAll(':scope > .shopify-section').forEach((section) => section.remove());

    let insertAfter = breadcrumbs || main;

    sectionsToInsert.forEach((html) => {
      insertAfter = this.insertSectionHtml(html, insertAfter);
    });

    return true;
  }

  syncMainSectionsFromDoc(doc) {
    const main = document.getElementById('MainContent');
    if (!main) return false;

    const targetSections = this.getTargetSections(doc);
    if (!targetSections.length) return false;

    const breadcrumbs = main.querySelector('.breadcrumbs');
    main.querySelectorAll(':scope > .shopify-section').forEach((section) => section.remove());

    let insertAfter = breadcrumbs || main;

    targetSections.forEach((section) => {
      const clone = document.importNode(section, true);
      insertAfter.insertAdjacentElement('afterend', clone);
      this.runScripts(clone);
      insertAfter = clone;
    });

    return true;
  }

  async fetchSectionsData(url, sectionIds, signal) {
    const sectionsData = {};
    const batchSize = 4;

    for (let index = 0; index < sectionIds.length; index += batchSize) {
      const batch = sectionIds.slice(index, index + batchSize);
      const sectionsUrl = new URL(url, window.location.origin);
      sectionsUrl.searchParams.set('sections', batch.join(','));

      try {
        const response = await fetch(sectionsUrl.toString(), {
          signal,
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) continue;

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) continue;

        const data = await response.json();
        Object.assign(sectionsData, data);
      } catch (error) {
        if (error.name === 'AbortError') throw error;
      }
    }

    return sectionsData;
  }

  finishNavigation(doc, url, pushState) {
    this.updateBreadcrumbs(doc);
    this.updateTitle(doc);

    if (pushState) {
      history.pushState({ subcollectionUrl: url }, '', url);
    }

    if (typeof initializeScrollAnimationTrigger === 'function') {
      const main = document.getElementById('MainContent');
      if (main) initializeScrollAnimationTrigger(main.innerHTML);
    }

    document.dispatchEvent(new CustomEvent('subcollection:loaded', { detail: { url } }));
  }

  async loadCollection(url, pushState = true) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    this.setLoading(true);

    try {
      const pageResponse = await fetch(url, {
        signal,
        credentials: 'same-origin',
      });

      if (!pageResponse.ok) {
        window.location.href = url;
        return;
      }

      const pageHtml = await pageResponse.text();
      const doc = new DOMParser().parseFromString(pageHtml, 'text/html');
      const targetSections = this.getTargetSections(doc);
      const targetSectionIds = targetSections
        .map((section) => this.getSectionId(section))
        .filter(Boolean);

      let synced = false;

      if (targetSectionIds.length) {
        const sectionsData = await this.fetchSectionsData(url, targetSectionIds, signal);
        synced = this.syncMainSections(targetSectionIds, sectionsData);
      }

      if (!synced) {
        synced = this.syncMainSectionsFromDoc(doc);
      }

      if (!synced) {
        window.location.href = url;
        return;
      }

      this.finishNavigation(doc, url, pushState);
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.href = url;
    } finally {
      this.setLoading(false);
    }
  }
}

function initSubcollectionsNav() {
  if (window.__subcollectionsNavInit) return;
  window.__subcollectionsNavInit = true;
  new SubcollectionsNav();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSubcollectionsNav);
} else {
  initSubcollectionsNav();
}
